import { motion } from "framer-motion";
import { Radio, Users, ShieldCheck, Network, Smartphone, Timer } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { SectionLabel, Reveal } from "./Reveal";

const glow = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const Tile = ({ className = "", children, i, testid }) => (
  <motion.div
    onMouseMove={glow}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className={`bento-tile ${className}`}
    data-testid={testid}
  >
    {children}
  </motion.div>
);

const Stat = ({ icon: Icon, item }) => (
  <div className="relative h-full flex flex-col justify-between gap-8">
    <Icon className="w-6 h-6 text-[var(--gold)]" />
    <div>
      {item.v && <div className="font-display text-3xl md:text-4xl text-[var(--text)]">{item.v}</div>}
      <div className="mt-2 text-[var(--text)] font-semibold">{item.t}</div>
      <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed">{item.d}</p>
    </div>
  </div>
);

export const Bento = () => {
  const { t } = useLang();
  const p = t.product;
  return (
    <section id="product" className="relative py-28 md:py-36 bg-[var(--surface-2)]" data-testid="product-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionLabel n="02">{p.label}</SectionLabel>
        <Reveal>
          <h2 className="font-display text-3xl md:text-5xl leading-[1.08] mt-8 max-w-4xl text-[var(--text)]">{p.title}</h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 md:auto-rows-[minmax(250px,auto)] gap-4">
          <Tile i={0} testid="bento-hub" className="md:col-span-2 md:row-span-2 !p-0 overflow-hidden bg-white">
            <div className="relative h-full min-h-[380px] flex flex-col">
              <div className="flex-1 relative overflow-hidden min-h-[320px] bg-[radial-gradient(circle_at_50%_42%,#2b3550_0%,#121824_55%,#0A0D14_100%)] grid place-items-center p-8">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(229,169,60,0.22),transparent_55%)]" aria-hidden="true" />
                <img loading="lazy" decoding="async" src="/assets/hub-device.webp" alt="QAZAQNET Hub" data-testid="bento-hub-photo" className="relative w-[78%] max-w-[400px] aspect-square object-contain rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] ring-1 ring-[var(--gold)]/50 float-slow" />
              </div>
              <div className="p-6 md:p-8 bg-[#0A0D14] text-[var(--text)]">
                <div className="font-display text-xl md:text-2xl">{p.hub.t}</div>
                <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed max-w-md">{p.hub.d}</p>
              </div>
            </div>
          </Tile>
          <Tile i={1} testid="bento-speed" className="md:col-span-2">
            <Stat icon={Radio} item={p.speed} />
            <div className="speed-bars absolute right-6 top-6 flex items-end gap-1.5 h-16" aria-hidden="true">
              {[30, 55, 40, 75, 60, 90, 100].map((h, k) => (
                <span key={k} style={{ height: `${h}%`, animationDelay: `${k * 0.12}s` }} />
              ))}
            </div>
          </Tile>
          <Tile i={2} testid="bento-users"><Stat icon={Users} item={p.users} /></Tile>
          <Tile i={3} testid="bento-ip"><Stat icon={ShieldCheck} item={p.ip} /></Tile>
          <Tile i={4} testid="bento-mesh" className="md:col-span-2">
            <Stat icon={Network} item={p.mesh} />
            <svg viewBox="0 0 160 80" className="absolute right-6 top-6 w-40 opacity-80" aria-hidden="true">
              <g stroke="var(--gold)" strokeWidth="1" fill="none" className="mesh-lines">
                <path d="M15 60 L55 20 L100 50 L145 15 M55 20 L145 15 M15 60 L100 50" />
              </g>
              {[[15, 60], [55, 20], [100, 50], [145, 15]].map(([x, y], k) => (
                <circle key={k} cx={x} cy={y} r="5" fill="var(--bg)" stroke="var(--gold)" strokeWidth="2" />
              ))}
            </svg>
          </Tile>
          <Tile i={5} testid="bento-cpe"><Stat icon={Smartphone} item={p.cpe} /></Tile>
          <Tile i={6} testid="bento-day"><Stat icon={Timer} item={p.day} /></Tile>
        </div>
      </div>
    </section>
  );
};
